import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";

import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import TooltipForTable from "../../../component-lib/Molecules/tooltip/TooltipForTable";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { searchIcon } from "../../../assets";
import ReportsService from "../../../services/ReportsService";
import SettingsService from "../../../services/SettingsService";
import VisitService from "../../../services/VisitService";
import ViewVisitNoteDialog from "../dialogs/ViewVisitNoteDialog";
import {
  hideLoader,
  showLoader,
} from "../../../component-lib/Molecules/loader/LoaderSlice";
import { eventEHRCustomToastMessage } from "../../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";
import { componentKey } from "../ReportsSlice";
import { getIncidentReport } from "../ReportsSaga";
import {
  CSV_HEADERS_INCIDENT,
  ROWS_PER_PAGE_OPTIONS,
  VISIT_TYPE_OPTIONS,
  formatIncidentVisitType,
  formatReportDate,
  toApiStartDate,
  toApiEndDate,
} from "../ReportConstants";
import { destructStatusData } from "../../../utils/statusConstants";
import DownloadCsvButton from "../components/DownloadCsvButton";
import StatusChip from "../components/StatusChip";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";

type IncidentReportTabProps = { facilityId: string };

const IncidentReportTab = ({ facilityId }: IncidentReportTabProps) => {
  const dispatch = useDispatch();
  const { incidentReport } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>,
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [searchedValue, setSearchedValue] = useState("");
  const [incidentLocation, setIncidentLocation] = useState("");
  const [visitType, setVisitType] = useState("");
  const [incidentLocationOptions, setIncidentLocationOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedLocation, setSelectedLocation] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [defaultLocationOptions, setDefaultLocationOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const locationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [openVisitNoteModal, setOpenVisitNoteModal] = useState(false);
  const [visitData, setVisitData] = useState<Record<string, any> | null>(null);
  const [selectedVisitNote, setSelectedVisitNote] = useState<Record<
    string,
    any
  > | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch the full visit and open the read-only visit-note preview.
  const handleViewVisitNote = async (visitId: string, note: any) => {
    if (!visitId) return;
    try {
      dispatch(showLoader());
      const response = await VisitService.getVisitById(visitId);
      const visit = response?.data?.visit || response?.data?.data || null;
      if (visit) {
        setVisitData(visit);
        setSelectedVisitNote(note);
        setOpenVisitNoteModal(true);
      } else {
        eventEHRCustomToastMessage({
          title: "Unable to load visit note",
          status: "error",
        });
      }
    } catch (error: any) {
      eventEHRCustomToastMessage({
        title: error?.response?.data?.message || "Failed to load visit note",
        status: "error",
      });
    } finally {
      dispatch(hideLoader());
    }
  };

  const rows: Array<Record<string, any>> = incidentReport?.data || [];

  // Fetch ALL incident-location pages and flatten them. The backend caps a
  // single request (even with limit "All") to the first page, so we page
  // through totalPages — otherwise locations beyond page 1 never show.
  const fetchAllIncidentLocations = async (searchValue?: string) => {
    const base: Record<string, any> = {
      limit: 100,
      type: "incident_location",
      status: "Active",
      ...(searchValue ? { searchValue } : {}),
    };
    const first = await SettingsService.getMasterDataList({
      ...base,
      pageNumber: 1,
    });
    let rows: Array<{ name?: string }> = first?.data?.masterData ?? [];
    const totalPages: number = first?.data?.totalPages ?? 1;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          SettingsService.getMasterDataList({
            ...base,
            pageNumber: i + 2,
          }).then((r: any) => r?.data?.masterData ?? [])
        )
      );
      rows = rows.concat(...rest);
    }
    return rows
      .filter((r) => r?.name)
      .map((r) => ({ label: r.name as string, value: r.name as string }));
  };

  // Load a default list of incident locations once, shown when the dropdown opens.
  useEffect(() => {
    const loadDefaultLocations = async () => {
      try {
        const opts = await fetchAllIncidentLocations();
        setDefaultLocationOptions(opts);
        setIncidentLocationOptions(opts);
      } catch {
        setDefaultLocationOptions([]);
        setIncidentLocationOptions([]);
      }
    };
    loadDefaultLocations();
  }, []);

  // Search incident-location master data server-side once the user types >= 3
  // chars; below that, fall back to the default list.
  const handleLocationSearch = (value: string) => {
    const q = value.trim();
    if (locationTimeoutRef.current) clearTimeout(locationTimeoutRef.current);
    if (q.length < 3) {
      setIncidentLocationOptions(defaultLocationOptions);
      return;
    }
    locationTimeoutRef.current = setTimeout(async () => {
      try {
        const opts = await fetchAllIncidentLocations(q);
        setIncidentLocationOptions(opts);
      } catch {
        setIncidentLocationOptions(defaultLocationOptions);
      }
    }, 400);
  };

  useEffect(() => {
    dispatch(
      getIncidentReport({
        pageNumber,
        limit: rowPerPage,
        facilityId: facilityId || undefined,
        searchValue: searchedValue?.trim() || undefined,
        incidentLocation: incidentLocation || undefined,
        visitType: visitType || undefined,
        startDate: toApiStartDate(startDate),
        endDate: toApiEndDate(endDate),
      }),
    );
  }, [
    facilityId,
    pageNumber,
    rowPerPage,
    searchedValue,
    incidentLocation,
    visitType,
    startDate,
    endDate,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setSearchedValue(value?.trim());
    }, 500);
  };

  const isAnyFilterApplied = Boolean(
    searchedValue || incidentLocation || visitType || startDate || endDate,
  );

  const handleClearFilters = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setSearchedValue("");
    setIncidentLocation("");
    setSelectedLocation(null);
    setIncidentLocationOptions(defaultLocationOptions);
    setVisitType("");
    setStartDate(null);
    setEndDate(null);
    setPageNumber(1);
  };

  const columns = [
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.patientName || "-"}</p>
      ),
    },
    {
      field: "bsaId",
      label: <div>BSA ID</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.bsaId || "-"}</p>
      ),
    },
    {
      field: "incidentLocation",
      label: <div>Incident Location</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.incidentLocation || "-"}
        </p>
      ),
    },
    {
      field: "visitType",
      label: <div>Visit Type</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {formatIncidentVisitType(row?.visitType)}
        </p>
      ),
    },
    {
      field: "chiefComplaint",
      label: <div>Chief Complaint</div>,
      renderLogic: (row: Record<string, any>) => (
        <TooltipForTable label={row?.chiefComplaint || "-"} />
      ),
    },
    {
      field: "incidentDate",
      label: <div>Incident Date</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {formatReportDate(row?.incidentDate)}
        </p>
      ),
    },
    {
      field: "facilityName",
      label: <div>Facility</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.facilityName || "-"}
        </p>
      ),
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: Record<string, any>) => (
        <StatusChip status={row?.status} />
      ),
    },
    {
      field: "action",
      label: <div>Actions</div>,
      renderLogic: (row: Record<string, any>) => (
        <div className="flex items-center">
          <MenuDropdown
            options={[
              ...(row?.visit_note || []).map((note: any, i: number) => ({
                label: `View Visit Note ${i + 1}`,
                onClickCb: () => handleViewVisitNote(row?.visitId, note),
              })),
            ]}
          />
        </div>
      ),
    },
  ];

  const fetchCsvRows = async () => {
    const response = await ReportsService.getIncidentReport({
      limit: "All",
      facilityId: facilityId || undefined,
      searchValue: searchedValue?.trim() || undefined,
      incidentLocation: incidentLocation || undefined,
      visitType: visitType || undefined,
      startDate: toApiStartDate(startDate),
      endDate: toApiEndDate(endDate),
    });
    const data: Array<Record<string, any>> = response?.data?.data || [];
    return data.map((row) => ({
      patientName: row?.patientName,
      bsaId: row?.bsaId,
      incidentLocation: row?.incidentLocation,
      visitType: formatIncidentVisitType(row?.visitType),
      chiefComplaint: row?.chiefComplaint,
      incidentDate: formatReportDate(row?.incidentDate),
      facilityName: row?.facilityName,
      status: destructStatusData(row?.status),
    }));
  };

  return (
    <div className="mt-5">
      <div className="flex flex-wrap mb-4 gap-3 justify-end items-center">
        <SearchSelectDropdown
          freeSolo={true}
          searchedValue={searchedValue}
          onSearchValueChangeCb={handleSearchChange}
          size="small"
          label="Search by Patient Name, BSA ID, Incident Location"
          fullWidth={"320px"}
          startIcon={
            <img
              alt="search-icon"
              src={searchIcon}
              width={"18px"}
              height={"18px"}
            />
          }
        />
        <div style={{ minWidth: "220px" }}>
          <Autocomplete
            size="small"
            options={incidentLocationOptions}
            filterOptions={(x) => x}
            getOptionLabel={(o) => o?.label || ""}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={selectedLocation}
            onChange={(_, newValue) => {
              setPageNumber(1);
              setSelectedLocation(newValue);
              setIncidentLocation(newValue?.value || "");
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === "reset") return;
              handleLocationSearch(newInputValue);
            }}
            noOptionsText="No locations found"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Incident Location"
                placeholder="Search location"
              />
            )}
          />
        </div>
        <div style={{ minWidth: "170px" }}>
          <SelectDropdown
            label="Visit Type"
            value={visitType}
            options={VISIT_TYPE_OPTIONS}
            onChangeCb={(e) => {
              setPageNumber(1);
              setVisitType(e.target.value);
            }}
          />
        </div>
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="From Date"
            value={startDate}
            maxDate={endDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setStartDate(value);
            }}
          />
        </div>
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="To Date"
            value={endDate}
            minDate={startDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setEndDate(value);
            }}
          />
        </div>
        <ClearFiltersButton
          onClear={handleClearFilters}
          disabled={!isAnyFilterApplied}
        />
        <DownloadCsvButton
          headers={CSV_HEADERS_INCIDENT}
          filename="incident-report.csv"
          fetchRows={fetchCsvRows}
          disabled={!rows.length}
        />
      </div>

      <Table
        columns={columns}
        rows={rows}
        noDataFoundMessage="No incident records found"
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: incidentReport?.totalPages,
          totalCount: incidentReport?.totalRecords,
          limit: rowPerPage,
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          onPageChange: (page: number) => setPageNumber(page),
          handleChangeRowsPerPage: (limitValue: string) => {
            setRowPerPage(limitValue);
            setPageNumber(1);
          },
        }}
      />

      <ViewVisitNoteDialog
        open={openVisitNoteModal}
        visitData={visitData}
        selectedNote={selectedVisitNote}
        handleClose={() => setOpenVisitNoteModal(false)}
      />
    </div>
  );
};

export default IncidentReportTab;
